import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, CheckCircle2, FileText } from "lucide-react";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-surface px-5 py-4 text-base text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/50";
const labelCls = "mb-2 block text-left text-base font-medium text-white/70";

const investorPerks = [
  ["inv.perk1", "Full investor brief and research roadmap"],
  ["inv.perk2", "Direct line to the founding team"],
  ["inv.perk3", "Data room and technical documentation on request"],
  ["inv.perk4", "Early visibility into the solutions pipeline"],
];

export default function Invest() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", organization: "", message: "" });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API}/leads/invest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-48 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(139,92,246,0.14),transparent)]" />
      <section data-testid="invest-page" className="relative z-10 mx-auto w-full max-w-6xl px-6 pb-28 pt-40 md:pt-48">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <span className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-mono text-base uppercase tracking-[0.2em] text-white/80">
            {t("inv.badge", "Investor Relations")}
          </span>
          <h1 className="mt-10 font-display text-[2rem] font-bold leading-[1.08] tracking-tight sm:text-[2.75rem] lg:text-[3.5rem]">
            {t("inv.h1a", "Invest in the")} <span className="text-gradient">{t("inv.h1b", "next 150 years.")}</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg font-light leading-relaxed text-fog">
            {t("inv.sub", "The Lifespan Project is a multidisciplinary human-longevity research initiative. Tell us who you are and our team will reach out with the full investor brief.")}
          </p>
        </motion.div>

        <div className="mt-16 grid items-start gap-6 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-6 lg:col-span-2"
          >
            <div data-testid="invest-perks-card" className="rounded-3xl border border-white/10 bg-surface p-8 text-left md:p-10">
              <h2 className="font-display text-2xl font-semibold tracking-tight">{t("inv.perks.h", "What investors receive")}</h2>
              <ul className="mt-6 grid gap-4">
                {investorPerks.map(([k, en]) => (
                  <li key={k} className="flex items-start gap-3 text-base font-light text-fog">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" />
                    {t(k, en)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-white/10 bg-surface/60 p-8 text-left md:p-10">
              <FileText className="h-6 w-6 text-cyan-300" />
              <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{t("inv.next.h", "What happens next")}</h3>
              <p className="mt-3 text-base font-light leading-relaxed text-fog">
                {t("inv.next.p", "You receive an instant branded acknowledgement with a copy of your enquiry. Our team then reaches out personally with the investor brief. Prefer email? Write to")}{" "}
                <a href="mailto:invest@thelifespanproject.com" className="text-gradient link-underline">
                  invest@thelifespanproject.com
                </a>
                .
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-gradient-to-br from-violet-500/40 via-blue-500/15 to-cyan-400/40 p-[1px] lg:col-span-3"
          >
            <div className="rounded-3xl bg-surface p-8 md:p-10">
              {status === "done" ? (
                <div data-testid="invest-success" className="py-10 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-cyan-300" />
                  <h2 className="mt-6 font-display text-2xl font-semibold tracking-tight">
                    {t("inv.success.h", "Thank you, request received.")}
                  </h2>
                  <p className="mx-auto mt-4 max-w-md text-lg font-light leading-relaxed text-fog">
                    {t("inv.success.p", "A branded acknowledgement with a copy of your enquiry is on its way to your inbox. Our team will reach out shortly.")}
                  </p>
                </div>
              ) : (
                <form data-testid="invest-form" onSubmit={submit} className="grid gap-6">
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="invest-name">{t("form.name", "Full name")}</label>
                      <input id="invest-name" data-testid="invest-name-input" required className={inputCls} value={form.name} onChange={set("name")} placeholder={t("form.ph.name", "Your name")} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="invest-email">{t("form.email", "Email")}</label>
                      <input id="invest-email" data-testid="invest-email-input" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@fund.com" />
                    </div>
                  </div>
                  <div className="grid gap-6 md:grid-cols-2">
                    <div>
                      <label className={labelCls} htmlFor="invest-phone">{t("form.phone", "Contact number")}</label>
                      <input id="invest-phone" data-testid="invest-phone-input" required className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="invest-org">{t("inv.org", "Organization")}</label>
                      <input id="invest-org" data-testid="invest-organization-input" required className={inputCls} value={form.organization} onChange={set("organization")} placeholder={t("inv.ph.org", "Fund, institution, or company")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="invest-message">{t("inv.interest", "Your interest")}</label>
                    <textarea id="invest-message" data-testid="invest-message-input" required rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder={t("inv.ph.interest", "Ticket size, mandate, timeline...")} />
                  </div>
                  {status === "error" && (
                    <p data-testid="invest-error" className="text-base text-rose-300">
                      {t("form.error", "Something went wrong. Please try again or email invest@thelifespanproject.com.")}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="invest-submit-button"
                    disabled={status === "sending"}
                    className="group flex items-center justify-center gap-3 rounded-full bg-gradient-accent px-6 py-3.5 text-base font-semibold text-white shadow-[0_8px_32px_rgba(59,130,246,0.35)] transition-shadow duration-300 hover:shadow-[0_8px_44px_rgba(34,211,238,0.5)] disabled:opacity-60 sm:px-8 sm:py-4"
                  >
                    {status === "sending" ? t("form.sending", "Sending...") : t("inv.submit", "Request the Investor Brief")}
                    <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
